import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Download, FileJson, MoreHorizontal, Plus, Search, Trash2 } from 'lucide-react';
import { useVersionStore } from '../../store/versionStore';
import type { Simulation } from '../../store/versionStore';
import { 
  exportSimulationToJSON, 
  importSimulationFromJSON, 
  downloadFile, 
  createSafeFilename 
} from '../../utils/exportImport';
import { errorService } from '../../services/errorService';
import { notificationService } from '../../services/notificationService';
import { confirmationService } from '../../services/confirmationService';
import { useSubscription } from '../subscription/SubscriptionContext';
import { UpgradeModal } from '../../components/UpgradeModal';
import { productMessaging } from '../../config/messaging';

export const SimulationsList = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<{ field: 'updated' | 'created' | 'name'; direction: 'asc' | 'desc' }>({
    field: 'updated',
    direction: 'desc',
  });
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<'simulation_limit' | 'assistant_limit' | 'read_only'>('simulation_limit');
  const [newSimName, setNewSimName] = useState('');
  const [newSimDescription, setNewSimDescription] = useState('');
  const [editingSimId, setEditingSimId] = useState<string | null>(null);
  const [copyingSimulationId, setCopyingSimulationId] = useState<string | null>(null);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [showInitialLoading, setShowInitialLoading] = useState(true);

  const { canCreateSimulation, isAdmin } = useSubscription();

  const simulations = useVersionStore((state) => state.simulations);
  const examples = useVersionStore((state) => state.examples);
  const loading = useVersionStore((state) => state.loading);
  const error = useVersionStore((state) => state.error);
  const fetchSimulations = useVersionStore((state) => state.fetchSimulations);
  const fetchExamples = useVersionStore((state) => state.fetchExamples);
  const fetchVersions = useVersionStore((state) => state.fetchVersions);
  const createSimulation = useVersionStore((state) => state.createSimulation);
  const updateSimulation = useVersionStore((state) => state.updateSimulation);
  const deleteSimulation = useVersionStore((state) => state.deleteSimulation);
  const getLatestVersion = useVersionStore((state) => state.getLatestVersion);
  const getSimulation = useVersionStore((state) => state.getSimulation);
  const saveVersion = useVersionStore((state) => state.saveVersion);
  const duplicateSimulation = useVersionStore((state) => state.duplicateSimulation);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchSimulations();
    fetchExamples();
    fetchVersions();
  }, [fetchSimulations, fetchExamples, fetchVersions]);

  useEffect(() => {
    if (!loading) {
      setShowInitialLoading(false);
      return;
    }

    const timeout = window.setTimeout(() => {
      setShowInitialLoading(false);
    }, 2500);

    return () => window.clearTimeout(timeout);
  }, [loading]);

  useEffect(() => {
    if (!openActionMenuId) return;

    const closeMenu = () => setOpenActionMenuId(null);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('click', closeMenu);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('click', closeMenu);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [openActionMenuId]);

  const handleCreateClick = () => {
    // Check if user can create a simulation (examples don't count towards limit)
    if (!canCreateSimulation(simulations.length, false)) {
      setUpgradeReason('simulation_limit');
      setShowUpgradeModal(true);
      return;
    }
    setShowCreateDialog(true);
  };

  const handleCreateSimulation = async () => {
    if (!newSimName.trim()) {
      notificationService.showWarning('Please enter a model name');
      return;
    }

    const sim = await createSimulation(newSimName.trim(), newSimDescription.trim() || undefined);
    if (sim) {
      setNewSimName('');
      setNewSimDescription('');
      setShowCreateDialog(false);
      navigate(`/simulations/${sim.id}`);
    }
  };

  const handleToggleExample = async (sim: Simulation) => {
    if (!isAdmin) return;
    
    const confirmMessage = sim.is_example
      ? `Remove "${sim.name}" from examples? It will become private again.`
      : `Make "${sim.name}" an example? It will be visible to all users (read-only for them).`;
    
    const confirmed = await confirmationService.confirm(confirmMessage);
    if (confirmed) {
      await updateSimulation(sim.id, {
        is_example: !sim.is_example,
      });
      // Refresh to update the lists
      await fetchSimulations();
    }
  };

  const handleDelete = async (sim: Simulation) => {
    const confirmed = await confirmationService.confirm(
      `Delete model "${sim.name}"? This will delete its saved history.`,
      { isDestructive: true, confirmText: 'Delete' }
    );
    if (confirmed) {
      await deleteSimulation(sim.id);
    }
  };

  const handleCopySimulation = async (sim: Simulation) => {
    // Respect subscription limits before copying (examples do not count toward the limit)
    if (!canCreateSimulation(simulations.length, false)) {
      setUpgradeReason('simulation_limit');
      setShowUpgradeModal(true);
      return;
    }

    setCopyingSimulationId(sim.id);
    try {
      const copied = await duplicateSimulation(sim.id);
      if (copied) {
        notificationService.showSuccess(`Model copied as "${copied.name}"`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to copy model';
      notificationService.showError(message);
    } finally {
      setCopyingSimulationId(null);
    }
  };

  const handleOpenWorkspace = (simulationId: string) => {
    const latestVersion = getLatestVersion(simulationId);

    if (latestVersion) {
      navigate(`/workspace?simulation=${simulationId}&version=${latestVersion.id}`);
    } else {
      navigate(`/workspace?simulation=${simulationId}`);
    }
  };

  const handleExport = (simulationId: string) => {
    const simulation = getSimulation(simulationId);
    const latestVersion = getLatestVersion(simulationId);

    if (!simulation || !latestVersion) {
      errorService.logError('Cannot export simulation', new Error('Simulation or version not found'), {
        action: 'exportSimulation',
        metadata: { simulationId }
      });
      return;
    }

    const json = exportSimulationToJSON(
      simulation.name,
      latestVersion.graph,
      simulation.description,
      simulation.tags
    );

    const filename = createSafeFilename(simulation.name, 'json');
    downloadFile(json, filename, 'application/json');
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const result = importSimulationFromJSON(text);

      if (!result.valid) {
        errorService.logError('Import failed', new Error(result.error), {
          action: 'importSimulation',
          metadata: { filename: file.name }
        });
        notificationService.showError(`Import failed: ${result.error}`);
        return;
      }

      // Create new simulation with imported data
      const newSim = await createSimulation(
        result.data.simulation.name,
        result.data.simulation.description,
        result.data.simulation.tags
      );

      if (!newSim) {
        errorService.logError('Failed to create simulation', new Error('Creation returned null'), {
          action: 'importSimulation'
        });
        return;
      }

      // Save imported graph as first version
      await saveVersion(newSim.id, result.data.simulation.graph, {
        name: 'Imported version',
        description: `Imported from ${file.name}`
      });

      // Navigate to the new simulation
      navigate(`/simulations/${newSim.id}`);
    } catch (error) {
      errorService.logError('Import failed', error, {
        action: 'importSimulation',
        metadata: { filename: file.name }
      });
      notificationService.showError('Failed to import simulation');
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date);
    }
  };

  // Filter simulations (exclude examples)
  const filteredSimulations = simulations
    .filter((sim) => !sim.is_example)
    .filter((sim) =>
      sim.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sim.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

  // Filter examples
  const filteredExamples = examples.filter((sim) =>
    sim.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sim.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort simulations
  const sortedSimulations = [...filteredSimulations].sort((a, b) => {
    let comparison = 0;
    switch (sortBy.field) {
      case 'updated':
        comparison = new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        break;
      case 'created':
        comparison = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        break;
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      default:
        return 0;
    }
    return sortBy.direction === 'asc' ? -comparison : comparison;
  });

  // Sort examples (by created date, newest first)
  const sortedExamples = [...filteredExamples].sort((a, b) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  // Combine: regular simulations first, then examples at the bottom
  const allSimulations = [...sortedSimulations, ...sortedExamples];
  const totalVisibleCount = allSimulations.length;
  const isWaitingForLibraryData = loading && simulations.length === 0 && examples.length === 0;

  const toggleSort = (field: 'updated' | 'created' | 'name') => {
    setSortBy((prev) => ({
      field,
      direction:
        prev.field === field
          ? (prev.direction === 'asc' ? 'desc' : 'asc')
          : field === 'name' ? 'asc' : 'desc',
    }));
  };

  const sortLabel = (field: 'updated' | 'created' | 'name') => {
    const label = field === 'updated' ? 'Last updated' : field === 'created' ? 'Created' : 'Model name';
    const nextDirection = sortBy.field === field && sortBy.direction === 'asc' ? 'descending' : 'ascending';
    return `Sort by ${label} ${nextDirection}`;
  };

  const renderSimulationTable = (items: Simulation[], options: { examples: boolean }) => (
    <table className="simulations-table">
      <colgroup>
        <col className="simulations-table-name-col" />
        <col className="simulations-table-date-col" />
        <col className="simulations-table-actions-col" />
      </colgroup>
      <thead>
        <tr>
          <th aria-sort={sortBy.field === 'name' ? (sortBy.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
            <button
              type="button"
              className={`sim-sort-button ${sortBy.field === 'name' ? 'sort-active' : ''}`}
              onClick={() => toggleSort('name')}
              aria-label={sortLabel('name')}
            >
              <span>Name</span>
              <span className="sort-arrow">{sortBy.field === 'name' ? (sortBy.direction === 'asc' ? '▲' : '▼') : '▴'}</span>
            </button>
          </th>
          <th aria-sort={sortBy.field === 'updated' ? (sortBy.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
            <button
              type="button"
              className={`sim-sort-button ${sortBy.field === 'updated' ? 'sort-active' : ''}`}
              onClick={() => toggleSort('updated')}
              aria-label={sortLabel('updated')}
            >
              <span>Last Updated</span>
              <span className="sort-arrow">{sortBy.field === 'updated' ? (sortBy.direction === 'desc' ? '▼' : '▲') : '▴'}</span>
            </button>
          </th>
          <th className="sim-actions-header">Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.map((sim) => {
          const isExample = options.examples || sim.is_example || examples.some((e) => e.id === sim.id);
          return (
            <tr
              key={sim.id}
              className={`simulation-row ${isExample ? 'example-row' : ''}`}
              onClick={() => handleOpenWorkspace(sim.id)}
            >
              <td className="sim-name-cell">
                <div className="sim-name">
                  {isExample && <span className="example-badge">Example</span>}
                  {sim.name}
                </div>
                {sim.description && <div className="sim-description">{sim.description}</div>}
              </td>
              <td className="sim-date">{formatDate(sim.updated_at || sim.created_at)}</td>
              <td className="sim-actions-cell">
                <div className="sim-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="action-menu-trigger"
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenActionMenuId((currentId) => (currentId === sim.id ? null : sim.id));
                    }}
                    aria-label={`Open actions for ${sim.name}`}
                    aria-haspopup="menu"
                    aria-expanded={openActionMenuId === sim.id}
                    title="More actions"
                  >
                    <MoreHorizontal size={17} aria-hidden="true" />
                  </button>
                  {openActionMenuId === sim.id && (
                    <div
                      className="action-menu"
                      role="menu"
                      aria-label={`Actions for ${sim.name}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <button
                        type="button"
                        role="menuitem"
                        className="action-menu-item"
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpenActionMenuId(null);
                          void handleCopySimulation(sim);
                        }}
                        disabled={copyingSimulationId === sim.id}
                      >
                        <Copy size={15} aria-hidden="true" />
                        <span>{copyingSimulationId === sim.id ? 'Copying...' : 'Copy'}</span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="action-menu-item"
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpenActionMenuId(null);
                          handleExport(sim.id);
                        }}
                      >
                        <Download size={15} aria-hidden="true" />
                        <span>Export</span>
                      </button>
                      {!isExample && (
                        <button
                          type="button"
                          role="menuitem"
                          className="action-menu-item action-menu-item-danger"
                          onClick={(event) => {
                            event.stopPropagation();
                            setOpenActionMenuId(null);
                            void handleDelete(sim);
                          }}
                        >
                          <Trash2 size={15} aria-hidden="true" />
                          <span>Delete</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  if (isWaitingForLibraryData && showInitialLoading) {
    return (
      <div className="simulations-container">
        <div className="loading-container">
          <div className="loading-spinner">Loading models...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="simulations-container">
      <div className="simulations-header">
        <div className="simulations-header-left">
          <h1>Models</h1>
          <p className="simulations-subtitle">{productMessaging.simulationLibrarySubtitle}</p>
        </div>
        <div className="simulations-header-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImportFile}
            style={{ display: 'none' }}
          />
          <button
            className="btn btn-secondary"
            onClick={handleImportClick}
            title="Import model from JSON file"
          >
            <FileJson size={16} aria-hidden="true" />
            Import
          </button>
          <button
            className="btn btn-primary create-simulation-btn"
            onClick={handleCreateClick}
          >
            <Plus size={16} aria-hidden="true" />
            New model
          </button>
        </div>
      </div>

      <div className="simulations-controls">
        <div className="search-box">
          <Search size={16} aria-hidden="true" />
          <input
            type="text"
            placeholder="Search models and examples..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {showCreateDialog && (
        <div className="modal-overlay" onClick={() => setShowCreateDialog(false)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>New model</h2>
            <div className="form-group">
              <label htmlFor="sim-name">Name *</label>
              <input
                id="sim-name"
                type="text"
                value={newSimName}
                onChange={(e) => setNewSimName(e.target.value)}
                placeholder="e.g., Q4 margin model"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="sim-description">Description</label>
              <textarea
                id="sim-description"
                value={newSimDescription}
                onChange={(e) => setNewSimDescription(e.target.value)}
                placeholder="Optional description..."
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button onClick={handleCreateSimulation} className="btn btn-primary">
                Create
              </button>
              <button onClick={() => setShowCreateDialog(false)} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showUpgradeModal && (
        <UpgradeModal
          reason={upgradeReason}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}

      <div className="simulations-list">
        {totalVisibleCount === 0 ? (
          <div
            className={`empty-state simulations-empty-state ${isWaitingForLibraryData ? 'is-syncing' : ''}`}
            aria-live={isWaitingForLibraryData ? 'polite' : undefined}
          >
            <h2>
              {searchQuery
                ? 'No matching models'
                : isWaitingForLibraryData
                  ? 'Preparing model library'
                  : productMessaging.emptyStates.simulationsTitle}
            </h2>
            <p>
              {searchQuery
                ? 'Try a different search term.'
                : isWaitingForLibraryData
                  ? 'Your library data is still syncing. You can start a new model or import JSON while it finishes.'
                  : productMessaging.emptyStates.simulationsBody}
            </p>
            {error && !isWaitingForLibraryData && <p className="empty-hint">{error}</p>}
            {searchQuery && <p className="empty-hint">Try adjusting your search query</p>}
            {!searchQuery && simulations.length === 0 && examples.length === 0 && (
              <div className="empty-state-actions">
                <button type="button" className="btn btn-primary" onClick={handleCreateClick}>
                  <Plus size={16} aria-hidden="true" />
                  New model
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleImportClick}>
                  <FileJson size={16} aria-hidden="true" />
                  Import JSON
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="simulation-library-sections">
            {sortedSimulations.length > 0 && (
              <section className="simulation-library-section" aria-labelledby="your-models-title">
                <div className="simulation-library-section-header">
                  <h2 id="your-models-title">Your models</h2>
                </div>
                {renderSimulationTable(sortedSimulations, { examples: false })}
              </section>
            )}
            {sortedExamples.length > 0 && (
              <section className="simulation-library-section" aria-labelledby="examples-title">
                <div className="simulation-library-section-header">
                  <h2 id="examples-title">Examples</h2>
                </div>
                {renderSimulationTable(sortedExamples, { examples: true })}
              </section>
            )}
          </div>
        )}
        {totalVisibleCount > 0 && (
          <div className="simulations-footer">
            {sortedSimulations.length > 0 && (
              <span>
                {sortedSimulations.length} model{sortedSimulations.length !== 1 ? 's' : ''}
              </span>
            )}
            {sortedSimulations.length > 0 && sortedExamples.length > 0 && <span> • </span>}
            {sortedExamples.length > 0 && (
              <span>
                {sortedExamples.length} example{sortedExamples.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
